import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet } from "react-native"

import { formatInstallationType } from "@/base/sensor-configurations"
import { formatSensorName, formatSensorState } from "@/base/sensor-formatting"
import { formatDeviceActionDisplayName } from "@/redux/named-device-actions.reducer"
import { getDeviceSummaryByDeviceIdFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppIcons,
  AppText,
  Badge,
  Box,
  getIconForInstallationType,
  Pressable,
  Row,
  SPACING,
  View,
} from "@/theme"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { Outline } from "@/theme/Outline"

import type { DeviceSummary } from "@/redux/device-configurations.reducer"
import type { EventActionTrigger, SelectDeviceHandler } from "@/redux/models"
import type { ViewProps } from "@/theme"
/**
 * Shows the given device's, name, installation type, and role in the device
 * connection
 */
function DeviceConnectionNameDisplay({
  deviceInstallationType,
  deviceName,
  deviceRole,
  textElement,
  ...rest
}: DeviceSummary &
  ViewProps & {
    deviceRole: "source" | "target"
    textElement: React.JSX.Element | null
  }): React.JSX.Element {
  const { t } = useTranslation("deviceConnections")
  return (
    <Outline style={styles.root} {...rest}>
      <Row flexWrap={"nowrap"} mb={"$2"}>
        <View style={styles.iconContainer}>
          {renderIconFromIconProp(
            getIconForInstallationType(deviceInstallationType),
          )}
        </View>
        <Box flex={1}>
          <Row justifyContent={"space-between"}>
            <AppText isTruncated flex={1} style={styles.nameText}>
              {deviceName}
            </AppText>
            <Badge isTruncated fontSize={"$xs"}>
              {deviceRole === "source" ? t("source") : t("target")}
            </Badge>
          </Row>
          <AppText colorScheme={"secondary"} fontSize={"$sm"}>
            {formatInstallationType(deviceInstallationType)}
          </AppText>
        </Box>
      </Row>
      {textElement}
    </Outline>
  )
}
const styles = StyleSheet.create({
  iconContainer: {
    marginRight: SPACING.$4,
  },
  nameText: { marginRight: SPACING.$2 },
  root: {
    marginHorizontal: SPACING.$1,
    marginVertical: SPACING.$1,
    overflow: "hidden",
    paddingVertical: SPACING.$1,
  },
})

// TODO: Simplify this further
export interface DeviceConnectionDisplayProps extends EventActionTrigger {
  onPressDevice?: SelectDeviceHandler
}

/**
 * Display a connection between two devices
 */
export function DeviceConnectionDisplay({
  delaySeconds,
  namedDeviceAction,
  onPressDevice,
  sourceDeviceId,
  sourceSensor,
  sourceSensorStateCurrent,
  sourceSensorStatePrevious,
  targetDeviceId,
}: DeviceConnectionDisplayProps) {
  const { t } = useTranslation("devices")

  const actionFormatted = formatDeviceActionDisplayName(namedDeviceAction)
  const sensorNameFormatted = formatSensorName(sourceSensor)
  const statePreviousFormatted = formatSensorState(sourceSensorStatePrevious, {
    labelLength: "long",
  })
  const stateCurrentFormatted = formatSensorState(sourceSensorStateCurrent, {
    labelLength: "long",
  })
  const sourceDevice = useRootSelector((state) => {
    return getDeviceSummaryByDeviceIdFromState(state, sourceDeviceId)
  })
  const targetDevice = useRootSelector((state) => {
    if (typeof targetDeviceId === "string") {
      return getDeviceSummaryByDeviceIdFromState(state, targetDeviceId)
    }
    return undefined
  })

  let sourceDeviceElement: React.ReactNode
  let targetDeviceElement: React.ReactNode | null = null

  const iconSize = 32
  if (sourceDevice) {
    sourceDeviceElement = (
      <DeviceConnectionNameDisplay
        deviceRole={"source"}
        textElement={
          <Row>
            <AppIcons.Signal size={iconSize} />
            <Box ml={"$1"}>
              <AppText colorScheme={"secondary"} fontSize={"$sm"}>
                {t("conditionLabel", { ns: "deviceActions" })}
              </AppText>
              <Row flexWrap={"nowrap"}>
                <Box mr={"$1"}>
                  <AppText>{sensorNameFormatted}</AppText>
                </Box>
                <AppText>{statePreviousFormatted}</AppText>
                <AppIcons.ArrowRight />
                <AppText>{stateCurrentFormatted}</AppText>
              </Row>
            </Box>
          </Row>
        }
        {...sourceDevice}
      />
    )
    if (onPressDevice) {
      sourceDeviceElement = (
        <Pressable onPress={() => onPressDevice(sourceDevice)}>
          {sourceDeviceElement}
        </Pressable>
      )
    }
    if (targetDevice) {
      // Format action text with delay if specified
      const actionDisplayText =
        typeof delaySeconds === "number" && delaySeconds > 0
          ? t("actionWithDelay", {
              ns: "deviceConnections",
              defaultValue: "Wait {{seconds}}s, then {{action}}",
              seconds: delaySeconds,
              action: actionFormatted,
            })
          : actionFormatted

      targetDeviceElement = (
        <DeviceConnectionNameDisplay
          deviceRole={"target"}
          textElement={
            typeof actionFormatted === "string" ? (
              <Row flexWrap={"nowrap"}>
                <AppIcons.DeviceCommandsRemote size={iconSize} />
                <Box ml={"$1"}>
                  <AppText colorScheme={"secondary"} fontSize={"$sm"}>
                    {t("actionLabel", { ns: "deviceActions" })}
                  </AppText>
                  <AppText>{actionDisplayText}</AppText>
                </Box>
              </Row>
            ) : null
          }
          {...targetDevice}
        />
      )
      if (onPressDevice) {
        targetDeviceElement = (
          <Pressable onPress={() => onPressDevice(targetDevice)}>
            {targetDeviceElement}
          </Pressable>
        )
      }
    }
  }

  return (
    <Row alignItems={"stretch"} mx={"$-2"}>
      <Box flex={1} minW={"$xs"} px={"$1"}>
        {sourceDeviceElement}
      </Box>
      <Box flex={1} minW={"$xs"} px={"$1"}>
        {targetDeviceElement}
      </Box>
    </Row>
  )
}
